import { groupBy, sortBy, uniq } from 'lodash/fp';
import { Plan } from '../../../../types/CompanyPaymentDto';
import type { CycleDto } from '../../../../types/CycleDto';
import type { ReviewCycleDto, ReviewCycleTimeline } from '../../../../types/ReviewCycleDto';
import api from '../../../api';
import type { ReviewCycleGuidedTimeline } from '../../features/Reviews/types';
import { concatAnd, truncateAndEllide } from '../Enumeration';

export function getCycleLimit(plan: Plan) {
    const cycleLimits: { [plan in Plan]?: number } = {
        [Plan.Launch]: 5,
    };
    return cycleLimits[plan] || null;
}

export const CYCLE_GROUPS = ['Upcoming', 'Current', 'Completed', 'Archived'] as const;
type CycleGroup = (typeof CYCLE_GROUPS)[number];

function sortCyclesByProcessEnds<T extends CycleDto>(cycles: T[]): T[] {
    return sortBy(cycle => cycle.timeline.processEnds, cycles).reverse();
}

function getCycleGroup(cycle: CycleDto): CycleGroup {
    if (cycle.archiveStatus?.isArchived) {
        return 'Archived';
    } else if (cycle.isAlreadyCompleted) {
        return 'Completed';
    } else if (cycle.isNotYetStarted) {
        return 'Upcoming';
    } else {
        return 'Current';
    }
}

export function groupCyclesByStatus<T extends CycleDto>(cycles: T[]): Partial<Record<CycleGroup, T[]>> {
    return groupBy(getCycleGroup, sortCyclesByProcessEnds(cycles));
}

/**
 * Shared prefix cut at the last separator, used to shorten repeated cycle-name prefixes.
 */
export function getCommonPrefix(strings: string[]): string {
    if (!strings || strings.length < 2) {
        return '';
    }

    let prefix = strings[0];
    for (let i = 1; i < strings.length; i++) {
        while (!strings[i].startsWith(prefix)) {
            prefix = prefix.slice(0, -1);
            if (!prefix) return '';
        }
    }

    let cutOff = -1;
    for (let i = 0; i < prefix.length; i++) {
        if (/[\s\-_:/.,\\]/.test(prefix[i])) {
            cutOff = i;
        }
    }

    return cutOff === -1 ? '' : prefix.substring(0, cutOff + 1);
}

const LABEL_CHAR_LIMIT = 70;

function shortenCycleName(name: string, commonPrefix: string): string {
    if (!commonPrefix) {
        return name;
    }
    const slicedName = name.slice(commonPrefix.length);
    return slicedName || name;
}

export function getCycleSelectionLabel(cycles: CycleDto[]): { label: string; title?: string } {
    const names = sortCyclesByProcessEnds(cycles).map(cycle => cycle.name);

    if (names.length === 1) {
        return { label: names[0] };
    }

    const commonPrefix = getCommonPrefix(names);
    const shortenedJoined = concatAnd(names.map(name => shortenCycleName(name, commonPrefix)));
    const fullOriginal = concatAnd(names);
    const label = truncateAndEllide(LABEL_CHAR_LIMIT, shortenedJoined);

    return {
        label,
        title: label !== fullOriginal ? fullOriginal : undefined,
    };
}

export type CyclesPerYear = Record<number, ReviewCycleDto[]>;

export async function getCyclesPerYear(): Promise<CyclesPerYear> {
    const cycles = await api.reviewCycles.list();
    return cycles.reduce<CyclesPerYear>((cyclesPerYear, cycle) => {
        const yearsOfCycle = getYearsOfCycleTimeline(cycle.timeline);
        yearsOfCycle.map(year => {
            if (!cyclesPerYear[year]) {
                cyclesPerYear[year] = [];
            }
            cyclesPerYear[year].push(cycle);
        });
        return cyclesPerYear;
    }, {});
}

/**
 * Determine the years of a cycle’s timeline that should be used for the cycle limit calculation.
 */
function getYearsOfCycleTimeline(timeline: ReviewCycleTimeline | ReviewCycleGuidedTimeline): number[] {
    return uniq([getYear(timeline.processStarts!), getYear(timeline.signBy!)]);
}

export function getYear(date: number | Date) {
    return new Date(date).getFullYear();
}
